import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface CustomerListRow {
  customerId?: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  branch: string;
  region: string;
  jobCount: number;
  openJobCount: number;
  totalValue: number;
  openIssueCount: number;
  lastActivity: string;
  hasActiveJobs: boolean;
  hasCompletedOnly: boolean;
}

export interface CustomersSummary {
  totalCustomers: number;
  withOpenJobs: number;
  withIssues: number;
  recentActivity: number;
  completedOnly: number;
  totalOpenJobs: number;
  totalIssues: number;
  totalValue: number;
  cities: string[];
  branches: string[];
  regions: string[];
}

export interface CustomersPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  view?: "all" | "active" | "issues" | "recent" | "completed";
  city?: string;
  branch?: string;
  region?: string;
  sortField?: "jobCount" | "totalValue" | "openIssueCount" | "lastActivity";
  sortDir?: "asc" | "desc";
  refreshToken?: number;
}

type CustomerListViewRow = {
  customer_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  address?: string | null;
  city?: string | null;
  branch?: string | null;
  region?: string | null;
  job_count?: number | string | null;
  open_job_count?: number | string | null;
  total_value?: number | string | null;
  open_issue_count?: number | string | null;
  last_activity?: string | null;
  has_active_jobs?: boolean | null;
  has_completed_only?: boolean | null;
};

type CustomersSummaryViewRow = {
  total_customers?: number | string | null;
  with_open_jobs?: number | string | null;
  with_issues?: number | string | null;
  recent_activity?: number | string | null;
  completed_only?: number | string | null;
  total_open_jobs?: number | string | null;
  total_issues?: number | string | null;
  total_value?: number | string | null;
  cities?: string[] | null;
  branches?: string[] | null;
  regions?: string[] | null;
};

const ROW_COLUMNS = [
  "customer_id",
  "customer_name",
  "customer_phone",
  "customer_email",
  "address",
  "city",
  "branch",
  "region",
  "job_count",
  "open_job_count",
  "total_value",
  "open_issue_count",
  "last_activity",
  "has_active_jobs",
  "has_completed_only",
].join(", ");

const SUMMARY_COLUMNS = [
  "total_customers",
  "with_open_jobs",
  "with_issues",
  "recent_activity",
  "completed_only",
  "total_open_jobs",
  "total_issues",
  "total_value",
  "cities",
  "branches",
  "regions",
].join(", ");

function num(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function recordMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function mapRow(row: CustomerListViewRow): CustomerListRow {
  return {
    customerId: String(row.customer_id ?? "").trim() || undefined,
    name: String(row.customer_name ?? "Unknown"),
    phone: String(row.customer_phone ?? ""),
    email: String(row.customer_email ?? ""),
    address: String(row.address ?? ""),
    city: String(row.city ?? ""),
    branch: String(row.branch ?? ""),
    region: String(row.region ?? ""),
    jobCount: num(row.job_count),
    openJobCount: num(row.open_job_count),
    totalValue: num(row.total_value),
    openIssueCount: num(row.open_issue_count),
    lastActivity: String(row.last_activity ?? ""),
    hasActiveJobs: row.has_active_jobs === true,
    hasCompletedOnly: row.has_completed_only === true,
  };
}

function mapSummary(row: CustomersSummaryViewRow | null | undefined): CustomersSummary {
  return {
    totalCustomers: num(row?.total_customers),
    withOpenJobs: num(row?.with_open_jobs),
    withIssues: num(row?.with_issues),
    recentActivity: num(row?.recent_activity),
    completedOnly: num(row?.completed_only),
    totalOpenJobs: num(row?.total_open_jobs),
    totalIssues: num(row?.total_issues),
    totalValue: num(row?.total_value),
    cities: Array.isArray(row?.cities) ? row!.cities.filter(Boolean) : [],
    branches: Array.isArray(row?.branches) ? row!.branches.filter(Boolean) : [],
    regions: Array.isArray(row?.regions) ? row!.regions.filter(Boolean) : [],
  };
}

function applySearch(request: any, search?: string) {
  const value = search?.trim();
  if (!value) return request;
  const sanitized = value.replace(/[%_]/g, "").replace(/,/g, " ");
  const pattern = `%${sanitized}%`;
  return request.or([
    `customer_name.ilike.${pattern}`,
    `customer_phone.ilike.${pattern}`,
    `customer_email.ilike.${pattern}`,
    `address.ilike.${pattern}`,
    `city.ilike.${pattern}`,
    `customer_id.ilike.${pattern}`,
  ].join(","));
}

function applyFilters(request: any, query: CustomersPageQuery) {
  if (query.view === "active") request = request.gt("open_job_count", 0);
  else if (query.view === "issues") request = request.gt("open_issue_count", 0);
  else if (query.view === "recent") request = request.gte("last_activity", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
  else if (query.view === "completed") request = request.eq("has_completed_only", true).eq("has_active_jobs", false);

  if (query.city && query.city !== "all") request = request.eq("city", query.city);
  if (query.branch && query.branch !== "all") request = request.eq("branch", query.branch);
  if (query.region && query.region !== "all") request = request.eq("region", query.region);

  return applySearch(request, query.search);
}

function applySort(request: any, query: CustomersPageQuery) {
  const ascending = query.sortDir === "asc";
  switch (query.sortField) {
    case "jobCount":
      return request.order("job_count", { ascending }).order("last_activity", { ascending: false });
    case "totalValue":
      return request.order("total_value", { ascending }).order("last_activity", { ascending: false });
    case "openIssueCount":
      return request.order("open_issue_count", { ascending }).order("last_activity", { ascending: false });
    case "lastActivity":
      return request.order("last_activity", { ascending });
    default:
      return request.order("sort_at", { ascending: false }).order("customer_name", { ascending: true });
  }
}

export const CustomersPageService = {
  async getCustomersPage(query: CustomersPageQuery): Promise<PageResponse<CustomerListRow>> {
    const key = `customers.page.${hashFilters(query)}`;
    try {
      return await withMetrics(key, async () => {
        const organizationId = await getCurrentOrganizationId();
        const page = Math.max(1, query.page);
        const pageSize = Math.max(1, query.pageSize);
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        let request = supabase
          .from("v_customers_list_ui")
          .select(ROW_COLUMNS, { count: "exact" })
          .eq("organization_id", organizationId)
          .range(from, to);

        request = applyFilters(request, query);
        request = applySort(request, query);

        const { data, error, count } = await request;
        if (error) throw error;
        return {
          data: ((data ?? []) as CustomerListViewRow[]).map(mapRow),
          count: count ?? 0,
          error: null,
        };
      });
    } catch (error) {
      console.warn("CustomersPageService.getCustomersPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: CustomersSummary; error: unknown | null }> {
    const key = `customers.summary.${refreshToken ?? 0}`;
    try {
      return await withMetrics(key, async () => {
        const organizationId = await getCurrentOrganizationId();
        const { data, error } = await supabase
          .from("v_customers_summary")
          .select(SUMMARY_COLUMNS)
          .eq("organization_id", organizationId)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as CustomersSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("CustomersPageService.getSummary failed:", error);
      return { data: mapSummary(null), error };
    }
  },
};
